import { useState } from 'react';
import {
  Button,
  Badge,
  Textarea,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Spinner,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Label,
} from '@rnd-payplus/alloy-react';
import { Sparkles, ArrowRight, Check, Upload, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { KPICard } from '@rnd-payplus/alloy-react/patterns';
import { usePageShell } from '../contexts/PageShellContext';

/** Embeddable content — no usePageShell, safe to render inside tabs. */
export function AIMappingContent() {
  const [sourceSample, setSourceSample] = useState('');
  const [targetSample, setTargetSample] = useState('');
  const [sourceFormat, setSourceFormat] = useState('auto');
  const [targetFormat, setTargetFormat] = useState('auto');
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [sourceFile, setSourceFile] = useState<File | null>(null);
  const [targetFile, setTargetFile] = useState<File | null>(null);

  const handleSourceFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSourceFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setSourceSample(e.target?.result as string);
      };
      reader.readAsText(file);
    }
  };

  const handleTargetFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setTargetFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setTargetSample(e.target?.result as string);
      };
      reader.readAsText(file);
    }
  };

  const handleAnalyze = async () => {
    if (!sourceSample || !targetSample) {
      setError('Please provide both source and target samples');
      return;
    }

    setAnalyzing(true);
    setError(null);

    try {
      const response = await fetch('/api/ai-mapping/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sourceSample,
          targetSample,
          sourceFormat,
          targetFormat
        })
      });

      const data = await response.json();

      if (data.success) {
        setResult(data);
      } else {
        setError(data.error || 'Analysis failed');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to analyze samples');
    }

    setAnalyzing(false);
  };

  const exampleSAP = `{
  "documentNumber": "F110000001",
  "runDate": "14.03.2024",
  "companyCode": "1000",
  "currency": "EUR",
  "name": "Test Company GmbH",
  "street": "Kolping Str. 15",
  "city": "Berlin",
  "postalCode": "12001",
  "country": "DE"
}`;

  const examplePay = `{
  "paymentReference": "",
  "currency": "",
  "beneficiaryName": "",
  "addressLine1": "",
  "city": "",
  "postalCode": "",
  "country": "",
  "amount": 0
}`;

  const suggestions = result?.suggestedMappings ?? [];
  const totalSuggestions = suggestions.length;
  const highConfidence = suggestions.filter((m: any) => m.confidence === 'high').length;
  const mediumConfidence = suggestions.filter((m: any) => m.confidence === 'medium').length;
  const lowConfidence = suggestions.filter((m: any) => m.confidence === 'low').length;

  return (
    <div>
      {/* KPI Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <KPICard title="Total" value={totalSuggestions} subtitle="Suggestions" />
        <KPICard title="High" value={highConfidence} subtitle="Confidence" />
        <KPICard title="Medium" value={mediumConfidence} subtitle="Confidence" />
        <KPICard title="Low" value={lowConfidence} subtitle="Confidence" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Source Sample */}
        <div className="space-y-2.5">
          <Label className="text-base font-medium">Source Sample System</Label>
          <Select value={sourceFormat} onValueChange={setSourceFormat}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Auto-Detect</SelectItem>
              <SelectItem value="json">JSON</SelectItem>
              <SelectItem value="csv">CSV</SelectItem>
              <SelectItem value="xml">XML</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <input
              type="file"
              accept=".json,.csv,.xml,.txt,.html"
              onChange={handleSourceFileUpload}
              className="hidden"
              id="source-file-upload"
            />
            <label htmlFor="source-file-upload">
              <Button variant="outline" size="sm" asChild>
                <span><Upload className="mr-1 h-4 w-4" /> Upload Source File</span>
              </Button>
            </label>
            {sourceFile && (
              <span className="text-xs text-muted-foreground">
                {sourceFile.name} ({(sourceFile.size / 1024).toFixed(1)} KB)
              </span>
            )}
          </div>
          <Textarea
            rows={8}
            value={sourceSample}
            onChange={(e) => setSourceSample(e.target.value)}
            placeholder={'Paste source data sample OR upload file above...\n\nExample (JSON):\n{\n  "documentNumber": "F110000001",\n  "currency": "EUR",\n  "name": "Company Name"\n}'}
            className="font-mono text-sm"
          />
          <Button
            variant="link"
            size="sm"
            onClick={() => setSourceSample(exampleSAP)}
          >
            Load SAP Example
          </Button>
        </div>

        {/* Target Sample */}
        <div className="space-y-2.5">
          <Label className="text-base font-medium">Target System Sample</Label>
          <Select value={targetFormat} onValueChange={setTargetFormat}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Auto-Detect</SelectItem>
              <SelectItem value="json">JSON</SelectItem>
              <SelectItem value="csv">CSV</SelectItem>
              <SelectItem value="xml">XML</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <input
              type="file"
              accept=".json,.csv,.xml,.txt,.html"
              onChange={handleTargetFileUpload}
              className="hidden"
              id="target-file-upload"
            />
            <label htmlFor="target-file-upload">
              <Button variant="outline" size="sm" asChild>
                <span><Upload className="mr-1 h-4 w-4" /> Upload Target File</span>
              </Button>
            </label>
            {targetFile && (
              <span className="text-xs text-muted-foreground">
                {targetFile.name} ({(targetFile.size / 1024).toFixed(1)} KB)
              </span>
            )}
          </div>
          <Textarea
            rows={8}
            value={targetSample}
            onChange={(e) => setTargetSample(e.target.value)}
            placeholder={'Paste target data structure OR upload file above...\n\nExample (JSON):\n{\n  "paymentReference": "",\n  "currency": "",\n  "beneficiaryName": ""\n}'}
            className="font-mono text-sm"
          />
          <Button
            variant="link"
            size="sm"
            onClick={() => setTargetSample(examplePay)}
          >
            Load Pay Backend Example
          </Button>
        </div>
      </div>

      {/* Analyze Button */}
      <div className="text-center my-6">
        <Button
          size="lg"
          onClick={handleAnalyze}
          disabled={!sourceSample || !targetSample || analyzing}
        >
          {analyzing ? <Spinner className="mr-2 h-5 w-5" /> : <Sparkles className="mr-2 h-5 w-5" />}
          {analyzing ? 'Analyzing...' : 'AI Analyze & Map Fields'}
        </Button>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 rounded-md border border-destructive bg-destructive/10 p-3 mb-4 text-destructive text-sm">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="space-y-5">
          <div>
            <h2 className="text-base font-medium leading-6 mb-4">Analysis Summary</h2>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <p className="text-sm text-muted-foreground">
                Source: {result.source.format.toUpperCase()} with {result.source.fields.length} fields
              </p>
              <p className="text-sm text-muted-foreground">
                Target: {result.target.format.toUpperCase()} with {result.target.fields.length} fields
              </p>
            </div>
            <div className="flex gap-2">
              <Badge variant="default" className="bg-green-600">{result.confidence.high} High Confidence</Badge>
              <Badge variant="secondary" className="bg-yellow-500 text-white">{result.confidence.medium} Medium</Badge>
              <Badge variant="outline">{result.confidence.low} Low</Badge>
            </div>
          </div>

          <div>
            <h2 className="text-base font-medium leading-6 mb-4">Suggested Field Mappings</h2>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Source Field</TableHead>
                    <TableHead></TableHead>
                    <TableHead>Target Field</TableHead>
                    <TableHead>Confidence</TableHead>
                    <TableHead>Score</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {result.suggestedMappings.map((mapping: any, idx: number) => (
                    <TableRow key={idx}>
                      <TableCell>{mapping.source}</TableCell>
                      <TableCell>
                        <ArrowRight className="h-4 w-4 text-primary" />
                      </TableCell>
                      <TableCell>{mapping.target}</TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            mapping.confidence === 'high' ? 'default' :
                            mapping.confidence === 'medium' ? 'secondary' : 'outline'
                          }
                          className={
                            mapping.confidence === 'high' ? 'bg-green-600' :
                            mapping.confidence === 'medium' ? 'bg-yellow-500 text-white' : ''
                          }
                        >
                          {mapping.confidence}
                        </Badge>
                      </TableCell>
                      <TableCell>{mapping.score}%</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {result.unmatchedSourceFields?.length > 0 && (
              <p className="mt-4 text-sm text-muted-foreground font-medium">
                Unmatched Source Fields: {result.unmatchedSourceFields.join(', ')}
              </p>
            )}

            {result.unmatchedTargetFields?.length > 0 && (
              <p className="mt-2 text-sm text-muted-foreground font-medium">
                Unmatched Target Fields: {result.unmatchedTargetFields.join(', ')}
              </p>
            )}

            <div className="mt-6">
              <Button
                  onClick={async () => {
                    try {
                      // Determine system names from analysis
                      const sourceName = result.source.format.toUpperCase() + ' System';
                      const targetName = result.target.format.toUpperCase() + ' System';

                      // Prompt for mapping name
                      const mappingName = prompt(
                        'Enter name for this mapping:',
                        `${sourceName} to ${targetName} - ${new Date().toLocaleDateString()}`
                      );

                      if (!mappingName) return;

                      // Save mapping to database
                      const response = await fetch('/api/mappings', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          name: mappingName,
                          sourceSystem: sourceName,
                          targetSystem: targetName,
                          fieldMappings: result.suggestedMappings.map((m: any) => ({
                            source: m.source,
                            target: m.target,
                            type: 'string',
                            confidence: m.confidence
                          }))
                        })
                      });

                      if (response.ok) {
                        toast.success(`Mapping "${mappingName}" saved successfully! You can now use it in the Integrations page.`);
                      } else {
                        toast.error('Failed to save mapping. Check console for details.');
                      }
                    } catch (error) {
                      console.error('Error saving mapping:', error);
                      toast.error('Error saving mapping: ' + error);
                    }
                  }}
                >
                  <Check className="mr-2 h-4 w-4" />
                  Save These Mappings
                </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const AIMappingPage = () => {
  usePageShell({
    title: 'AI-Powered Field Mapper',
    breadcrumbs: [
      { label: 'Home', href: '/dashboard' },
      { label: 'AI Mapper' },
    ],
  });

  return <AIMappingContent />;
};

export default AIMappingPage;
